import { createFileRoute } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { apiJson } from '@/lib/api';
import { track } from '@/lib/analytics';

type PlanFunnelTotals = {
  opened: number;
  substantive: number;
  completed: number;
  co_parent_sent: number;
  claimed: number;
  pairs: number;
};

type PlanFunnelRates = {
  start_rate: number;
  completion_rate: number;
  co_parent_rate: number;
  claim_rate: number;
};

type PlanFunnelDay = {
  date: string;
  opened: number;
  substantive: number;
  completed: number;
  co_parent_sent: number;
  claimed: number;
};

type PlanFunnelResponse = {
  ok: boolean;
  days: number;
  totals: PlanFunnelTotals;
  rates: PlanFunnelRates;
  daily: PlanFunnelDay[];
};

const RANGE_OPTIONS = [7, 30, 90] as const;
type RangeDays = (typeof RANGE_OPTIONS)[number];

function formatCount(value: number): string {
  return value.toLocaleString();
}

function formatPercent(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return '0%';
  return `${Math.round(value * 1000) / 10}%`;
}

function StatTile({
  label,
  value,
  hint,
  accent,
}: {
  label: string;
  value: string;
  hint?: string;
  accent?: string;
}) {
  return (
    <div className='rounded-lg border border-nova-border bg-white p-4'>
      <p className='text-xs font-medium uppercase tracking-wide text-nova-muted-foreground'>
        {label}
      </p>
      <p className='mt-1 text-2xl font-semibold text-nova-foreground' style={accent ? { color: accent } : undefined}>
        {value}
      </p>
      {hint ? <p className='mt-1 text-xs text-nova-muted-foreground'>{hint}</p> : null}
    </div>
  );
}

function FunnelRow({
  label,
  count,
  of,
  rate,
  color,
}: {
  label: string;
  count: number;
  of: number;
  rate: number;
  color: string;
}) {
  const width = of > 0 ? Math.max(2, Math.round((count / of) * 100)) : 0;
  return (
    <div className='space-y-1'>
      <div className='flex items-baseline justify-between text-sm'>
        <span className='font-medium text-nova-foreground'>{label}</span>
        <span className='text-nova-muted-foreground'>
          {formatCount(count)}
          {of > 0 ? ` · ${formatPercent(rate)}` : ''}
        </span>
      </div>
      <div className='h-3 w-full overflow-hidden rounded-full bg-nova-muted'>
        <div className='h-full rounded-full' style={{ width: `${width}%`, backgroundColor: color }} />
      </div>
    </div>
  );
}

function AdminPlanBuilderPage() {
  const { getToken } = useAuth();
  const [range, setRange] = useState<RangeDays>(30);

  useEffect(() => {
    track('Admin Plan Builder Screen');
  }, []);

  const funnelQuery = useQuery({
    queryKey: ['admin', 'plan-builder-funnel', range],
    queryFn: () =>
      apiJson<PlanFunnelResponse>(
        getToken,
        `/v1/admin/parenting-plan/funnel?days=${range}`,
      ),
  });

  const data = funnelQuery.data;
  const t = data?.totals;
  const r = data?.rates;

  return (
    <div className='space-y-5'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div className='min-w-0'>
          <h1 className='text-xl font-semibold text-nova-foreground sm:text-2xl'>
            Plan Builder
          </h1>
          <p className='mt-1 text-sm text-nova-muted-foreground'>
            Funnel for the public parenting plan builder, counted server-side from
            <code className='mx-1 rounded bg-nova-muted px-1'>parenting_plan_drafts</code>.
            Drafts are created on open, so &ldquo;opens&rdquo; ~= builder opens.
          </p>
        </div>
        <div className='flex gap-1 rounded-lg border border-nova-border bg-white p-1'>
          {RANGE_OPTIONS.map((d) => (
            <button
              key={d}
              type='button'
              onClick={() => setRange(d)}
              className={`rounded-md px-3 py-1 text-sm font-medium ${
                range === d
                  ? 'bg-nova-primary text-white'
                  : 'text-nova-muted-foreground hover:text-nova-foreground'
              }`}
            >
              {d}d
            </button>
          ))}
        </div>
      </div>

      {funnelQuery.isLoading ? (
        <div className='flex justify-center py-12'>
          <div className='h-10 w-10 animate-spin rounded-full border-2 border-nova-primary border-t-transparent' />
        </div>
      ) : funnelQuery.isError || !data || !t || !r ? (
        <p className='text-red-600'>Could not load the plan builder funnel.</p>
      ) : (
        <>
          <div className='grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4'>
            <StatTile
              label='Builder opens'
              value={formatCount(t.opened)}
              hint={`last ${data.days}d · drafts created on open`}
            />
            <StatTile
              label='Substantive starts'
              value={formatCount(t.substantive)}
              hint={`${formatPercent(r.start_rate)} of opens`}
            />
            <StatTile
              label='Completed (PDF + email)'
              value={formatCount(t.completed)}
              hint={`${formatPercent(r.completion_rate)} of starts`}
            />
            <StatTile
              label='Pairs sourced'
              value={formatCount(t.pairs)}
              hint='claimers in a 2-member household'
              accent='#059669'
            />
          </div>

          <div className='rounded-lg border border-nova-border bg-white p-5'>
            <h2 className='mb-4 text-sm font-semibold uppercase tracking-wide text-nova-muted-foreground'>
              Funnel
            </h2>
            <div className='space-y-4'>
              <FunnelRow
                label='Opens'
                count={t.opened}
                of={t.opened}
                rate={1}
                color='#94a3b8'
              />
              <FunnelRow
                label='Substantive starts'
                count={t.substantive}
                of={t.opened}
                rate={r.start_rate}
                color='#0284c7'
              />
              <FunnelRow
                label='Completed (PDF + email)'
                count={t.completed}
                of={t.substantive}
                rate={r.completion_rate}
                color='#2b6d6b'
              />
              <FunnelRow
                label='Co-parent sent'
                count={t.co_parent_sent}
                of={t.completed}
                rate={r.co_parent_rate}
                color='#7c3aed'
              />
              <FunnelRow
                label='Claimed into an account'
                count={t.claimed}
                of={t.completed}
                rate={r.claim_rate}
                color='#ea580c'
              />
              <FunnelRow
                label='Pairs sourced'
                count={t.pairs}
                of={t.claimed}
                rate={t.claimed > 0 ? t.pairs / t.claimed : 0}
                color='#059669'
              />
            </div>
          </div>

          <div className='rounded-lg border border-nova-border bg-white p-5'>
            <h2 className='mb-3 text-sm font-semibold uppercase tracking-wide text-nova-muted-foreground'>
              Daily
            </h2>
            {data.daily.length === 0 ? (
              <p className='text-sm text-nova-muted-foreground'>No builder activity in this range.</p>
            ) : (
              <div className='overflow-x-auto'>
                <table className='w-full text-sm'>
                  <thead>
                    <tr className='border-b border-nova-border text-left text-xs uppercase tracking-wide text-nova-muted-foreground'>
                      <th className='py-2 pr-4'>Date</th>
                      <th className='py-2 pr-4 text-right'>Opens</th>
                      <th className='py-2 pr-4 text-right'>Starts</th>
                      <th className='py-2 pr-4 text-right'>Completed</th>
                      <th className='py-2 pr-4 text-right'>Co-parent</th>
                      <th className='py-2 text-right'>Claimed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[...data.daily].reverse().map((row) => (
                      <tr key={row.date} className='border-b border-nova-border/60'>
                        <td className='py-2 pr-4 text-nova-foreground'>{row.date}</td>
                        <td className='py-2 pr-4 text-right'>{formatCount(row.opened)}</td>
                        <td className='py-2 pr-4 text-right'>{formatCount(row.substantive)}</td>
                        <td className='py-2 pr-4 text-right'>{formatCount(row.completed)}</td>
                        <td className='py-2 pr-4 text-right'>{formatCount(row.co_parent_sent)}</td>
                        <td className='py-2 text-right'>{formatCount(row.claimed)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

export const Route = createFileRoute('/admin/plan-builder')({
  component: AdminPlanBuilderPage,
});
